import { createGetApiHook, createPostApiHook } from "@/shared/api/apiHooks";
import { axiosInstance } from "@/shared/api/axiosConfig";
import type { APIResponseType } from "@/shared/types";

export type MainCardContributionRecord = {
  id?: number | string;
  amount?: number;
  message?: string | null;
  status?: string | null;
  purchase_type?: string | null;
  purchase_id?: number | string | null;
  created_at?: string | null;
  contributed_at?: string | null;
  user?: {
    id?: number;
    username?: string;
    first_name?: string;
    last_name?: string;
    profile_image?: string | null;
  } | null;
  contributor_name?: string | null;
  name?: string | null;
  username?: string | null;
};

export type MainCardContributionList =
  | MainCardContributionRecord[]
  | {
      items?: MainCardContributionRecord[];
      results?: MainCardContributionRecord[];
      contributions?: MainCardContributionRecord[];
      data?: MainCardContributionRecord[];
      total?: number;
      count?: number;
      total_count?: number;
    };

export type GetMainCardContributionsParams = {
  purchase_id?: number;
  main_card_id?: number;
  offset?: number;
  limit?: number;
  headers?: Record<string, string>;
};

export type CreateMainCardContributionPayload = {
  main_card_id: number;
  amount: number;
  message: string;
  purchase_type: string;
  status?: string;
  purchase_id?: number;
};


export type VerifyImpactContributionPayload = {
  contributionId: number | string;
  purchaseId: number | string;
  talentId: number | string;
  status: "Verified" | "Rejected";
  verifiedAmount?: number;
  note?: string;
};
const CONTRIBUTIONS_API = "/main-card/contributions";

const getMainCardContributionsAPI = (queryParams: GetMainCardContributionsParams = {}): string => {
  const { headers: _headers, ...rest } = queryParams;
  const params = new URLSearchParams();

  if (rest.main_card_id != null && Number(rest.main_card_id) > 0) {
    params.set("main_card_id", String(rest.main_card_id));
  }
  if (rest.purchase_id != null && Number(rest.purchase_id) > 0) {
    params.set("purchase_id", String(rest.purchase_id));
  }
  params.set("offset", String(rest.offset ?? 0));
  params.set("limit", String(rest.limit ?? 100));

  return `${CONTRIBUTIONS_API}?${params.toString()}`;
};

export const useGetMainCardContributions = createGetApiHook<
  APIResponseType<MainCardContributionList>,
  GetMainCardContributionsParams
>(getMainCardContributionsAPI);

export const useCreateMainCardContribution = createPostApiHook<
  CreateMainCardContributionPayload,
  { headers?: Record<string, string> },
  APIResponseType<MainCardContributionRecord>
>(CONTRIBUTIONS_API, {
  isFormDataMethod: false,
});

export type InviteImpactPartnerPayload = {
  mainCardId: number;
  userId: number;
  talentId: number | string;
};

export async function inviteImpactPartner({
  mainCardId,
  userId,
  talentId,
}: InviteImpactPartnerPayload) {
  return axiosInstance.post(
    `/main-card/${mainCardId}/invite-partner`,
    { user_id: userId },
    {
      headers: {
        "talent-id": String(talentId),
      },
    }
  );
}

export type ImpactPageVerifyStatus = "Verified" | "Rejected";

export type VerifyImpactPagePayload = {
  pageId: number;
  talentId: number | string;
  status: ImpactPageVerifyStatus;
};

export async function verifyImpactPage({ pageId, talentId, status }: VerifyImpactPagePayload) {
  return axiosInstance.patch(
    `/main-card/pages/${pageId}/verify`,
    { status },
    {
      headers: {
        "talent-id": String(talentId),
      },
    }
  );
}

export async function verifyImpactContribution(input: {
  contributionId: number | string;
  talentId: number | string;
  status: "Verified" | "Rejected";
  verifiedAmount?: number;
  note?: string;
  purchaseId?: number | string;
}) {
  const body =
    input.status === "Rejected"
      ? { status: "rejected", verification_note: input.note }
      : {
          verified_amount: input.verifiedAmount,
          ...(input.note ? { verification_note: input.note } : {}),
        };

  return axiosInstance.post(
    `/main-card/contribution/${encodeURIComponent(String(input.contributionId))}/verify`,
    body,
    {
      headers: {
        "talent-id": String(input.talentId),
      },
    }
  );
}

export function readApiErrorMessage(error: unknown, fallback: string): string {
  const data = (error as { response?: { data?: { message?: string; detail?: string } } })
    ?.response?.data;
  if (typeof data?.message === "string" && data.message.trim()) return data.message;
  if (typeof data?.detail === "string" && data.detail.trim()) return data.detail;
  return (error as Error)?.message || fallback;
}
